/**
 * TagDefinitionSchema — hand-authored Zod schema mirroring the tag_definitions table.
 *
 * Traceability: REQ-2.22
 */
import { z } from 'zod';

const TagDateFields = {
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
};

const TagDefinitionBaseSchema = z.object({
  id: z.number(),
  code: z.string(),
  label: z.string(),
  isClientVisible: z.boolean(),
  isActive: z.boolean(),
  sortOrder: z.number().nullable(),
  createdAt: z.date(),
  updatedAt: z.date(),
});

export const TagDefinitionSchema = TagDefinitionBaseSchema.extend(TagDateFields);
export type TagDefinition = z.infer<typeof TagDefinitionSchema>;

/**
 * Lightweight `{ code, label }` projection of a tag definition — the shape
 * actually returned by list queries that project tags via
 * `unitTagsJsonExpr` (`packages/db/src/queries/media-units/primary-variant-media-type.ts`),
 * e.g. offer-units list rows. Distinct from `TagDefinitionSchema` (the full
 * row shape used by the media-catalog list, which batch-fetches complete
 * `tag_definitions` rows separately). Use this — not `TagDefinitionSchema` —
 * for any response whose tags come from `unitTagsJsonExpr`.
 */
export const TagChipSchema = z.object({
  code: z.string(),
  label: z.string(),
});
export type TagChip = z.infer<typeof TagChipSchema>;

export const CreateTagSchema = z.object({
  code: z.string().min(1).max(255),
  label: z.string().min(1).max(255),
  isClientVisible: z.boolean().optional().default(false),
  sortOrder: z.number().int().optional(),
});
export type CreateTag = z.infer<typeof CreateTagSchema>;

export const UpdateTagSchema = z.object({
  label: z.string().min(1).max(255).optional(),
  isClientVisible: z.boolean().optional(),
  sortOrder: z.number().int().nullable().optional(),
});
export type UpdateTag = z.infer<typeof UpdateTagSchema>;

/** Effects of permanently deleting a tag: the count of media units that currently carry it. */
export const DeleteTagEffectsSchema = z.object({
  affectedUnitCount: z.number(),
});
export type DeleteTagEffects = z.infer<typeof DeleteTagEffectsSchema>;
